import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { SiteInfo, Projet, GalerieMedia, ApiResponse, BatimentConfigurationPublic, HeroSlider, Actualite } from '../types/api';

// Interface pour les messages de contact
export interface ContactMessageData {
  nom: string;
  prenoms: string;
  email: string;
  telephone: string;
  sujet: string;
  message: string;
  projet_interesse?: string;
}

class PublicApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // ===== SITE INFO ENDPOINTS =====

  /**
   * Récupère toutes les informations du site visibles publiquement
   */
  async getSiteInfos(params?: {
    type_info?: string;
    search?: string;
    ordering?: string;
  }): Promise<ApiResponse<SiteInfo>> {
    const response: AxiosResponse<ApiResponse<SiteInfo>> = await this.api.get('/public-site-infos/', { params });
    return response.data;
  }

  /**
   * Récupère les informations du site par type
   */
  async getSiteInfosByType(typeInfo: string): Promise<SiteInfo[]> {
    const response: AxiosResponse<SiteInfo[]> = await this.api.get('/public-site-infos/by_type/', {
      params: { type_info: typeInfo }
    });
    return response.data;
  }

  /**
   * Récupère tous les textes dynamiques pour le frontend
   */
  async getTexts(): Promise<SiteInfo[]> {
    // Utiliser l'endpoint non paginé pour récupérer tous les textes en une seule requête
    const response: AxiosResponse<SiteInfo[]> = await this.api.get('/public-site-infos/all/');
    return response.data;
  }

  /**
   * Récupère les données pour la page d'accueil (HOME, CONTACT, SOCIAL)
   */
  async getHomeData(): Promise<{
    home: SiteInfo[];
    contact: SiteInfo[];
    social: SiteInfo[];
  }> {
    const response: AxiosResponse<{
      home: SiteInfo[];
      contact: SiteInfo[];
      social: SiteInfo[];
    }> = await this.api.get('/public-site-infos/home_data/');
    return response.data;
  }

  // ===== PROJET ENDPOINTS =====

  /**
   * Récupère tous les projets visibles publiquement
   */
  async getProjets(params?: {
    statut_projet?: string;
    promoteur?: string;
    search?: string;
    ordering?: string;
  }): Promise<Projet[]> {
    const response: AxiosResponse<ApiResponse<Projet>> = await this.api.get('/public-projets/', { params });
    return response.data.results || [];
  }

  /**
   * Récupère un projet spécifique par ID
   */
  async getProjet(id: number): Promise<Projet> {
    const response: AxiosResponse<Projet> = await this.api.get(`/public-projets/${id}/`);
    return response.data;
  }

  /**
   * Récupère la galerie d'un projet
   */
  async getProjetGalerie(id: number): Promise<GalerieMedia[]> {
    const response: AxiosResponse<GalerieMedia[]> = await this.api.get(`/public-projets/${id}/galerie/`);
    return response.data;
  }

  /**
   * Récupère l'URL de l'image principale d'un projet
   */
  async getProjetImagePrincipale(id: number): Promise<{ image_url: string }> {
    const response: AxiosResponse<{ image_url: string }> = await this.api.get(`/public-projets/${id}/image_principale/`);
    return response.data;
  }

  /**
   * Récupère les statistiques d'un projet
   */
  async getProjetStatistiques(id: number): Promise<{
    total_batiments: number;
    types_maison_total: number;
    total_logements: number;
    logements_disponibles: number;
    souscriptions_total: number;
    revenus_total: number;
    surface_totale: number;
    budget_total: number;
    taux_occupation: number;
  }> {
    const response: AxiosResponse<{
      total_batiments: number;
      types_maison_total: number;
      total_logements: number;
      logements_disponibles: number;
      souscriptions_total: number;
      revenus_total: number;
      surface_totale: number;
      budget_total: number;
      taux_occupation: number;
    }> = await this.api.get(`/public-projets/${id}/statistiques/`);
    return response.data;
  }

  /**
   * Récupère les configurations de maisons disponibles pour un projet (nouveau système uniquement)
   */
  async getProjetMaisonsDisponibles(id: number): Promise<BatimentConfigurationPublic[]> {
    const response: AxiosResponse<BatimentConfigurationPublic[]> = await this.api.get(`/public-projets/${id}/maisons_disponibles/`);
    return response.data;
  }

  /**
   * Récupère les statistiques globales de tous les projets
   */
  async getGlobalStatistiques(): Promise<{
    total_projets: number;
    projets_en_cours: number;
    projets_termines: number;
    total_logements: number;
    total_surface: number;
    total_souscriptions: number;
    total_revenus: number;
    taux_occupation_global: number;
    logements_disponibles: number;
  }> {
    const response: AxiosResponse<{
      total_projets: number;
      projets_en_cours: number;
      projets_termines: number;
      total_logements: number;
      total_surface: number;
      total_souscriptions: number;
      total_revenus: number;
      taux_occupation_global: number;
      logements_disponibles: number;
    }> = await this.api.get('/public-projets/all_statistiques/');
    return response.data;
  }

  // ===== CONTENU STATIQUE ENDPOINTS =====

  /**
   * Récupère les slides hero publiés (pour le slider hero)
   */
  async getHeroSliders(): Promise<ApiResponse<HeroSlider>> {
    const response: AxiosResponse<ApiResponse<HeroSlider>> = await this.api.get('/public-hero-sliders/');
    return response.data;
  }

  /**
   * Récupère la liste des actualités publiques
   */
  async getActualites(params?: {
    projet?: number;
    afficher_accueil?: boolean;
    search?: string;
    ordering?: string;
    page?: number;
  }): Promise<ApiResponse<Actualite> | Actualite[]> {
    const response: AxiosResponse<ApiResponse<Actualite> | Actualite[]> = await this.api.get('/public-actualites/', {
      params,
    });
    return response.data;
  }

  /**
   * Récupère une actualité spécifique via son slug
   */
  async getActualite(slug: string): Promise<Actualite> {
    const response: AxiosResponse<Actualite> = await this.api.get(`/public-actualites/${slug}/`);
    return response.data;
  }

  // ===== UTILITY METHODS =====

  /**
   * Récupère les informations de contact
   */
  async getContactInfo(): Promise<SiteInfo[]> {
    return this.getSiteInfosByType('CONTACT');
  }

  /**
   * Récupère les liens des réseaux sociaux
   */
  async getSocialLinks(): Promise<SiteInfo[]> {
    return this.getSiteInfosByType('SOCIAL');
  }

  /**
   * Récupère les informations de la page d'accueil
   */
  async getHomeInfo(): Promise<SiteInfo[]> {
    return this.getSiteInfosByType('HOME');
  }

  /**
   * Récupère les informations légales
   */
  async getLegalInfo(): Promise<SiteInfo[]> {
    return this.getSiteInfosByType('LEGAL');
  }

  /**
   * Récupère les informations "À propos"
   */
  async getAboutInfo(): Promise<SiteInfo[]> {
    return this.getSiteInfosByType('ABOUT');
  }

  /**
   * Récupère les employés à afficher sur la page À propos
   */
  async getEmployes(): Promise<Array<{
    id: number;
    full_name: string;
    fonction: string;
    image_url: string | null;
    ordre: number;
  }>> {
    const response: AxiosResponse<Array<{
      id: number;
      full_name: string;
      fonction: string;
      image_url: string | null;
      ordre: number;
    }>> = await this.api.get('/public-employes/');
    return response.data;
  }

  // ===== CONTACT MESSAGES ENDPOINTS =====

  /**
   * Envoie un message de contact
   */
  async sendContactMessage(messageData: ContactMessageData): Promise<{
    message: string;
    id: number;
    statut: string;
  }> {
    const response: AxiosResponse<{
      message: string;
      id: number;
      statut: string;
    }> = await this.api.post('/contact-messages/', messageData);
    return response.data;
  }
}

// Export d'une instance singleton
export const publicApiService = new PublicApiService();
export default publicApiService;
